import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { qk } from "@/lib/query/keys";
import type { SavedLayoutNav, SavedLayoutNode, WorkspaceLayout } from "@/lib/types";

const selectLayouts = (data: { layouts: WorkspaceLayout[] }) => data.layouts;
const NO_LAYOUTS: WorkspaceLayout[] = [];

/** The saved window layouts (one list for the installation), kept current by the live feed. */
export function useLayouts(): WorkspaceLayout[] {
  const query = useQuery({ queryKey: qk.layouts(), queryFn: api.listLayouts, select: selectLayouts });
  return query.data ?? NO_LAYOUTS;
}

type LayoutOp =
  | { kind: "save"; name: string; layout: SavedLayoutNode; nav: SavedLayoutNav | null }
  | { kind: "replace"; layoutId: string; name?: string; layout: SavedLayoutNode; nav: SavedLayoutNav | null }
  | { kind: "rename"; layoutId: string; name: string }
  | { kind: "delete"; layoutId: string };

const FAILED: Record<LayoutOp["kind"], string> = {
  save: "save the layout",
  replace: "update the layout",
  rename: "rename the layout",
  delete: "delete the layout",
};

/** Saving, overwriting, renaming and deleting layouts; each says so in a toast, and the list refetches after. */
export function useLayoutActions() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (op: LayoutOp): Promise<{ id: string; name: string }> => {
      if (op.kind === "save") return api.createLayout({ name: op.name, layout: op.layout, nav: op.nav });
      if (op.kind === "replace") return api.updateLayout(op.layoutId, { layout: op.layout, nav: op.nav });
      if (op.kind === "rename") return api.updateLayout(op.layoutId, { name: op.name });
      return api.deleteLayout(op.layoutId);
    },
    onSuccess: (_result, op) => {
      if (op.kind === "save") toast.success(`Saved layout “${op.name}”`);
      else if (op.kind === "replace") toast.success(op.name ? `Saved “${op.name}” with these windows` : "Layout updated to these windows");
      else if (op.kind === "delete") toast.success("Layout deleted");
    },
    onError: (cause, op) => toast.error(cause instanceof ApiError ? cause.message : `Could not ${FAILED[op.kind]}`),
    onSettled: () => client.invalidateQueries({ queryKey: qk.layouts() }),
  });
  return { run: mutation.mutateAsync, pending: mutation.isPending };
}
